import axios from 'axios';
import { axiosPost } from 'services';
import { resolvePdfSource, sanitizePdfFileName } from 'utils/pdfDownload';

const GRAPHQL_ENDPOINT =
  process.env.REACT_APP_GRAPHQL_ENDPOINT ||
  process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT ||
  'https://api-dev.polystox.co.in/graphql';
const REST_API_ENDPOINT =
  process.env.REACT_APP_REST_API_ENDPOINT ||
  process.env.NEXT_PUBLIC_RESTAPI_ENDPOINT ||
  'https://api-dev.polystox.co.in/api/';

// In-memory caches to avoid refetching for the same search
const tradersCache = new Map();
const tradersPromiseCache = new Map();
let demandsCache = null;
let offersCache = null;

const getAuthToken = () => {
  try {
    const polystoxAdmin = localStorage.getItem('polystox-admin');
    if (polystoxAdmin) {
      const adminData = JSON.parse(polystoxAdmin);
      if (adminData && adminData.token) {
        return adminData.token;
      }
    }

    const sources = [
      localStorage.getItem('authToken'),
      localStorage.getItem('token'),
      localStorage.getItem('access_token'),
      localStorage.getItem('accessToken'),
      localStorage.getItem('jwt'),
      sessionStorage.getItem('authToken'),
      sessionStorage.getItem('token'),
      process.env.REACT_APP_API_TOKEN,
    ];

    const token = sources.find(
      (t) => t !== null && t !== 'undefined' && t !== '',
    );

    if (token) {
      return token;
    }

    return null;
  } catch (error) {
    return process.env.REACT_APP_API_TOKEN || null;
  }
};

const graphqlClient = axios.create({
  baseURL: GRAPHQL_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

graphqlClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      const updatedConfig = {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        },
      };
      return updatedConfig;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

graphqlClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const normalizeSearchTerm = (value = '') => String(value || '').trim();

const restClient = axios.create({
  baseURL: REST_API_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const fetchTraders = (searchTerm = '', pageNo = 1, pageSize = 50) => {
  const normalizedSearch = normalizeSearchTerm(searchTerm);
  const cacheKey = normalizedSearch.toLowerCase();

  if (tradersCache.has(cacheKey)) {
    return tradersCache.get(cacheKey);
  }

  if (!tradersPromiseCache.has(cacheKey)) {
    const query = `
      query Traders($filter: [FilterMap], $pageNo: Int, $pageSize: Int) {
        traders(filter: $filter, pageNo: $pageNo, pageSize: $pageSize) {
          traders {
            id
            firstName
            lastName
            entityName
            tradeName
            phone
            callingCode {
              callingCode
            }
            addresses {
              id
              houseNumber
              street
              area
              pinCode
              city {
                id
                cityName
              }
              state {
                id
                stateName
              }
              country {
                id
                country
              }
            }
          }
          totalCount
        }
      }
    `;

    const variables = {
      filter: normalizedSearch
        ? [
            {
              key: 'traderSearchFilter',
              value: normalizedSearch,
              type: 'STRING',
              op: 'LIKE',
            },
          ]
        : [],
      pageNo,
      pageSize,
    };

    const request = graphqlClient
      .post('', { query, variables })
      .then((response) => {
        if (response.data.errors) {
          throw new Error(response.data.errors[0].message);
        }
        const traders = response.data?.data?.traders?.traders || [];
        tradersCache.set(cacheKey, traders);
        return traders;
      })
      .finally(() => {
        tradersPromiseCache.delete(cacheKey);
      });

    tradersPromiseCache.set(cacheKey, request);
  }

  return tradersPromiseCache.get(cacheKey);
};

export const getCachedTraders = (searchTerm = '') => {
  const normalizedSearch = normalizeSearchTerm(searchTerm);
  return tradersCache.get(normalizedSearch.toLowerCase()) || null;
};

export const fetchGrades = async (searchString = '') => {
  const response = await restClient.get('grades/get-grades-with-filters', {
    params: {
      productIds: '',
      subCategoryIds: '',
      manufacturer: '',
      origin: '',
      searchString: searchString || '',
      pageNumber: 1,
      pageSize: 50,
    },
  });
  const { data } = response;
  return data.data;
};

export const fetchActiveDemands = async (forceRefresh = false) => {
  if (demandsCache && !forceRefresh) {
    return demandsCache;
  }

  const query = `
    query GetActiveDemands {
      demands(filter: [{ key: "status", value: "ACTIVE", type: STRING, op: EQ }], pageNo: 1, pageSize: 50) {
        demands {
          id
          createdAt
          rate
          totalQty
          quantityUnit
          actualQuantityUnit
          city {
            id
            cityName
          }
          state {
            id
            stateName
          }
          grades {
            id
            name
          }
        }
        totalCount
      }
    }
  `;

  const response = await graphqlClient.post('', { query });

  if (response.data.errors) {
    throw new Error(response.data.errors[0].message);
  }

  demandsCache = response.data.data.demands.demands;
  return demandsCache;
};

export const fetchActiveOffers = async (forceRefresh = false) => {
  if (offersCache && !forceRefresh) {
    return offersCache;
  }

  const query = `
    query GetActiveOffers {
      offers(filter: [{ key: "status", value: "ACTIVE", type: STRING, op: EQ }], pageNo: 1, pageSize: 50) {
        offers {
          id
          createdAt
          rate
          totalQty
          minimumQty
          quantityUnit
          actualQuantityUnit
          city {
            id
            cityName
          }
          state {
            id
            stateName
          }
          grades {
            id
            name
          }
        }
        totalCount
      }
    }
  `;

  const response = await graphqlClient.post('', { query });

  if (response.data.errors) {
    throw new Error(response.data.errors[0].message);
  }

  offersCache = response.data.data.offers.offers;
  return offersCache;
};

export const prefetchAddOrderData = async () => {
  try {
    await Promise.all([
      fetchTraders(),
      fetchActiveDemands(),
      fetchActiveOffers(),
    ]);
  } catch (error) {
    // Do not block the page if prefetch fails; form will retry on demand.
    // eslint-disable-next-line no-console
    console.error('Prefetch add order data failed', error);
  }
};

export const getCachedDemands = () => demandsCache;
export const getCachedOffers = () => offersCache;

export const createAdminOrder = async (orderData) => {
  const mutation = `
    mutation CreateAdminManualOrder($order: AdminOrderInput!) {
      createAdminManualOrder(order: $order) {
        id
        orderSequenceNumber
        createdAt
        status
        orderValue
        gstAmount
        total
        dueAmount
        quantity
        quantityUnit
        rate
        grade {
          id
          name
        }
        buyer {
          id
          firstName
          middleName
          lastName
          entityName
        }
        seller {
          id
          firstName
          middleName
          lastName
          entityName
        }
        deliveryAddress {
          id
          houseNumber
          street
          area
          city {
            id
            cityName
          }
          state {
            id
            stateName
          }
          pinCode
        }
        billingAddress {
          id
          houseNumber
          street
          area
          city {
            id
            cityName
          }
          state {
            id
            stateName
          }
          pinCode
        }
      }
    }
  `;

  const variables = {
    order: orderData,
  };

  const response = await graphqlClient.post('', {
    query: mutation,
    variables,
  });

  if (response.data.errors) {
    throw new Error(response.data.errors[0].message);
  }

  return response.data.data.createAdminManualOrder;
};

export const downloadProformaInvoiceBase64 = async (orderId) => {
  const query = `
    query DownloadPI($orderId: ID!) {
      downloadProformaInvoice(orderId: $orderId)
    }
  `;

  const response = await graphqlClient.post('', {
    query,
    variables: { orderId: String(orderId) },
  });

  if (response.data.errors?.length) {
    throw new Error(response.data.errors[0].message);
  }

  const invoiceSource = response?.data?.data?.downloadProformaInvoice;
  if (!invoiceSource) {
    throw new Error('PI document was not generated.');
  }

  return invoiceSource;
};

export const downloadPurchaseOrderBase64 = async (orderId) => {
  const query = `
    query DownloadPO($orderId: ID!) {
      downloadPurchaseOrder(orderId: $orderId)
    }
  `;

  const response = await graphqlClient.post('', {
    query,
    variables: { orderId: String(orderId) },
  });

  if (response.data.errors?.length) {
    throw new Error(response.data.errors[0].message);
  }

  const invoiceSource = response?.data?.data?.downloadPurchaseOrder;
  if (!invoiceSource) {
    throw new Error('PO document was not generated.');
  }

  return invoiceSource;
};

export const generateOrderPi = async (orderId, options = {}) => {
  if (!orderId) {
    throw new Error('Order ID is required to generate PI.');
  }

  const { orderSequenceNumber } = options;
  const base64Pdf = await downloadProformaInvoiceBase64(orderId);
  const safeFileName = `proforma-invoice-${orderSequenceNumber || orderId}.pdf`;

  return {
    fileName: safeFileName,
    fileSource: base64Pdf,
  };
};

export const generateOrderPo = async (orderId, options = {}) => {
  if (!orderId) {
    throw new Error('Order ID is required to generate PO.');
  }

  const { orderSequenceNumber } = options;
  const base64Pdf = await downloadPurchaseOrderBase64(orderId);
  const safeFileName = `purchase-order-${orderSequenceNumber || orderId}.pdf`;

  return {
    fileName: safeFileName,
    fileSource: base64Pdf,
  };
};

export const uploadGeneratedOrderDocument = async (
  documentSource,
  fileName,
  auth,
) => {
  if (!auth?.data?.token) {
    throw new Error('Authorization token not available.');
  }

  const resolvedSource = resolvePdfSource(documentSource);
  if (!resolvedSource) {
    throw new Error('Document source is not available.');
  }

  const documentResponse = await fetch(resolvedSource);
  if (!documentResponse.ok) {
    throw new Error('Unable to prepare document for upload.');
  }

  const blob = await documentResponse.blob();
  const uploadFile = new File(
    [blob],
    sanitizePdfFileName(fileName || 'document.pdf'),
    { type: 'application/pdf' },
  );

  const uploadFormData = new FormData();
  uploadFormData.append('file', uploadFile);

  const uploadResponse = await axiosPost(
    `${REST_API_ENDPOINT}common/upload-file?containerName=assets`,
    uploadFormData,
    {},
    auth,
  );

  if (!uploadResponse?.isSuccessful) {
    throw new Error(
      uploadResponse?.message || 'Failed to upload generated document.',
    );
  }

  const uploadedUrl = uploadResponse?.data?.data;
  if (!uploadedUrl) {
    throw new Error('Generated document URL was not returned.');
  }

  return uploadedUrl;
};

export const saveGeneratedOrderDocumentUrl = async (
  orderId,
  key,
  documentUrl,
  auth,
) => {
  if (!auth?.data?.token) {
    throw new Error('Authorization token not available.');
  }

  const response = await axios.put(
    `${REST_API_ENDPOINT}order/update-pipo/${orderId}?key=${key}&url=${encodeURIComponent(documentUrl)}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${auth?.data?.token}`,
        'Content-Type': 'application/json',
      },
    },
  );

  if (!response?.data?.isSuccessful) {
    throw new Error(
      response?.data?.message || `Failed to save ${key} document URL.`,
    );
  }

  return response?.data;
};
